import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import { ChangeEvent, ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { AlertTriangle, BarChart2, Calendar, ChevronDown, ChevronUp, Eye, EyeOff, Filter, Image, Lock, Mail as MailIcon, Trash, Unlock } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useData, useMutation, useServerErrors } from '../../../Shared/api';
import { Editor } from '../../../Shared/Editor/Editor';
import { useLocale } from '../../../Shared/locale';
import { Modal } from '../../../Shared/Modal/Modal';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { Event, Mail, Project, ProjectType, Ticket, User } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { Checkbox } from '../../../Shared/UI/Checkbox';
import CheckboxLabel from '../../../Shared/UI/CheckboxLabel';
import { ConfirmButton } from '../../../Shared/UI/ConfirmButton';
import { DateInput } from '../../../Shared/UI/DateInput';
import DateRange from '../../../Shared/UI/DateRange';
import Delimit from '../../../Shared/UI/Delimit';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { FadeIn } from '../../../Shared/UI/FadeIn';
import { FileInput } from '../../../Shared/UI/FileInput';
import { Icon } from '../../../Shared/UI/Icon';
import InfoMessage from '../../../Shared/UI/InfoMessage';
import { Input } from '../../../Shared/UI/Input';
import InputDescription from '../../../Shared/UI/InputDescription';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import { InputWrapper } from '../../../Shared/UI/InputWrapper';
import { PickerItem } from '../../../Shared/UI/Picker';
import { Radio } from '../../../Shared/UI/Radio';
import RadioLabel from '../../../Shared/UI/RadioLabel';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { TimeInput } from '../../../Shared/UI/TimeInput';
import { Toggle } from '../../../Shared/UI/Toggle';
import { useCharityStyle } from '../../../Shared/useCharityStyle';
import { useCurrencyInput } from '../../../Shared/useCurrencyInput';
import { useIntegerInput } from '../../../Shared/useIntegerInput';
import useStickyElement from '../../../Shared/useStickyElement';
import { useRouter } from '../../router';
import { Charity, Product } from '../../types';
import Layout from '../../UI/Layout';
import MailEditor from './MailEditor';
import { ProjectLayout } from './ProjectLayout';

export interface EditCustomMailFormProps {
  mail: Mail & {
    tickets: Ticket[];
    products: Product[];
    campaigns_count: number;
    sent_count: number;
    complete: boolean;
    deletable: boolean;
  };
  project: Project & {
    custom_mail_variables: string[];
    event: (Event & {
      tickets: Ticket[];
    }) | null;
  };
  products: PickerItem[];
  charity: Charity;
  user: User;
}

export default function EditCustomMailForm({
  mail,
  project,
  products,
  charity,
  user,
}: EditCustomMailFormProps) {
  const { t } = useTranslation();
  const { handleCurrencyBlur, formatCurrency: formatInputCurrency, parseCurrency } = useCurrencyInput();
  const { handleIntegerBlur, formatInteger: formatInputInteger, parseInteger } = useIntegerInput();
  const { formatNumber, formatCurrency, formatDate, formatRelativeDate, toIsoDateTimeString } = useLocale();
  const { routes } = useRouter();
  const notifier = useNotifier();

  useCharityStyle(charity);

  const defaultSender = project.mail_sender || charity?.mail_sender || charity.title || '';
  const projectTickets = project.event?.tickets || [];

  const form = useForm({
    defaultValues: {
      title: mail.title,
      header: null,
      delete_header: false,
      header_link: mail.header_link,
      subject: mail.subject,
      sender: mail.sender,
      body: mail.body,
      include_individuals: mail.include_individuals,
      include_teams: mail.include_teams,
      tickets: mail.tickets.map((ticket) => ticket.id),
      products: mail.products.map((product) => product.id),
      min_days_old: mail.min_days_old !== null ? formatInputInteger(mail.min_days_old) : '',
      days_before_start: mail.days_before_start !== null ? formatInputInteger(mail.days_before_start) : '',
      days_after_end: mail.days_after_end !== null ? formatInputInteger(mail.days_after_end) : '',
      created_after_date: mail.created_after ? formatDate(mail.created_after, 'internal_date') : null,
      created_after_time: mail.created_after ? formatDate(mail.created_after, 'internal_time') : null,
      min_funds_raised: mail.min_funds_raised !== null ? formatInputCurrency(mail.min_funds_raised) : '',
      max_funds_raised: mail.max_funds_raised !== null ? formatInputCurrency(mail.max_funds_raised) : '',
      ignore_opt_outs: mail.ignore_opt_outs,
      continuous: mail.start_at === null || mail.end_at !== null,
      start_date: mail.start_at ? formatDate(mail.start_at, 'internal_date') : null,
      start_time: mail.start_at ? formatDate(mail.start_at, 'internal_time') : null,
      end_date: mail.end_at ? formatDate(mail.end_at, 'internal_date') : null,
      end_time: mail.end_at ? formatDate(mail.end_at, 'internal_time') : null,
    },
  });

  const {
    register, control, watch, setValue, setError, handleSubmit, resetField,
    formState: { errors, defaultValues, isDirty },
  } = form;

  const isDraft = (!mail.enabled && isDirty) || !mail.complete;

  const minDaysOld = watch('min_days_old') ? parseInteger(watch('min_days_old')) : null;
  const daysBeforeStart = watch('days_before_start') ? parseInteger(watch('days_before_start')) : null;
  const daysAfterEnd = watch('days_after_end') ? parseInteger(watch('days_after_end')) : null;
  const createdAfterDate = watch('created_after_date');
  const createdAfterTime = watch('created_after_time');
  const minFundsRaised = watch('min_funds_raised') ? parseCurrency(watch('min_funds_raised')) : null;
  const maxFundsRaised = watch('max_funds_raised') ? parseCurrency(watch('max_funds_raised')) : null;
  const includeIndividuals = watch('include_individuals');
  const includeTeams = watch('include_teams');
  const ignoreOptOuts = watch('ignore_opt_outs');
  const tickets = watch('tickets');
  const selectedProducts = watch('products');

  const startDate = watch('start_date');
  const endDate = watch('end_date');
  const continuous = watch('continuous');

  const body = watch('body');

  const currentMailHeaderUrl = mail.header_url || project.mail_header_url || charity.mail_header_url;
  const [mailHeader, setMailHeader] = useState<File>();
  const deleteMailHeader = watch('delete_header');
  const canDeleteMailHeader = !deleteMailHeader && (!!mailHeader || !!mail.header_url);

  const handleMailHeaderChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMailHeader(event.target.files?.[0] || undefined);
    resetField('header'); // Reset errors
    setValue('delete_header', false);
    setMailHeaderSrc(undefined);
  };

  const unsetMailHeader = () => {
    setMailHeader(undefined);
    resetField('header'); // Reset errors
    setValue('delete_header', true);
    setMailHeaderSrc(undefined);
  };

  const [mailHeaderSrc, setMailHeaderSrc] = useState<string>();

  useEffect(() => {
    if (mailHeader) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setMailHeaderSrc(reader.result?.toString() || undefined));
      reader.readAsDataURL(mailHeader);
    }
  }, [mailHeader]);

  const validateMailHeader = (event: SyntheticEvent<HTMLImageElement>) => {
    const element = event.currentTarget;

    if (element.naturalWidth < 570) {
      unsetMailHeader();
      setError('header', { message: t('shared:validation.image_too_small') });
    }
  };

  const createdAfter = createdAfterDate && toIsoDateTimeString(createdAfterDate, createdAfterTime || '00:00', 'internal_date_time');

  const [campaignsData, {
    loading: loadingRecipients,
    refetch: refetchRecipients,
  }] = useData<{ count: number; }>(
    routes.custom_mail_campaigns_json(project.id),
    {
      params: {
        minDaysOld,
        daysBeforeStart,
        daysAfterEnd,
        createdAfter,
        minFundsRaised,
        maxFundsRaised,
        includeIndividuals: includeIndividuals ? 1 : 0,
        includeTeams: includeTeams ? 1 : 0,
        ignoreOptOuts: ignoreOptOuts ? 1 : 0,
        tickets,
        products: selectedProducts,
      },
      debounce: 300,
    }
  );

  const campaignsCount = campaignsData ? campaignsData.count : mail.campaigns_count;

  const [editMail, { loading: saving, errors: serverErrors }] = useMutation(
    routes.edit_mail(mail.id),
    {
      preserveState: 'errors',
      onSuccess: () => {
        notifier.notify(t('backend:mail_saved'));
      },
    }
  );

  useServerErrors(form, serverErrors);

  const [toggleMail, { loading: toggling }] = useMutation(
    routes.toggle_mail(mail.id), {
      preserveScroll: true,
      onSuccess: () => {
        notifier.notify(
          !mail.enabled ? t('backend:mail_enabled') : t('backend:mail_disabled'),
          !mail.enabled ? Eye : EyeOff
        );
      },
      onError: () => {
        notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
      },
    }
  );

  const [deleteMail, { loading: deleting }] = useMutation(routes.delete_mail(mail.id), {
    onSuccess: () => {
      notifier.notify(t('backend:mail_deleted'));
    },
    onError: () => {
      notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
    },
  });

  const loading = saving || toggling || deleting;

  const [showOptOuts, setShowOptOuts] = useState(mail.ignore_opt_outs);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const handleToggle = () => {
    if (!mail.enabled) {
      refetchRecipients();
      setShowConfirmationModal(true);
    } else {
      toggleMail({
        enabled: false,
      });
    }
  };

  const handleEnableMail = () => {
    toggleMail({ enabled: true });
    setShowConfirmationModal(false);
  };

  const submit = async (data: typeof defaultValues) => {
    await editMail({
      title: data?.title,
      subject: data?.subject,
      sender: data?.sender,
      header: mailHeader,
      delete_header: deleteMailHeader,
      header_link: data?.header_link,
      body: data?.body,
      include_individuals: data?.include_individuals,
      include_teams: data?.include_teams,
      ignore_opt_outs: data?.ignore_opt_outs,
      tickets: data?.tickets,
      products: data?.products,
      min_days_old: data?.min_days_old && parseInteger(data.min_days_old),
      days_before_start: data?.days_before_start && parseInteger(data.days_before_start),
      days_after_end: data?.days_after_end && parseInteger(data.days_after_end),
      created_after: data?.created_after_date && toIsoDateTimeString(data?.created_after_date, data?.created_after_time || '00:00', 'internal_date_time'),
      min_funds_raised: data?.min_funds_raised && parseCurrency(data.min_funds_raised),
      max_funds_raised: data?.max_funds_raised && parseCurrency(data.max_funds_raised),
      start_at: data?.start_date && toIsoDateTimeString(data?.start_date, data?.start_time || '00:00', 'internal_date_time'),
      end_at: data?.continuous && data?.end_date ? toIsoDateTimeString(data?.end_date, data?.end_time || '00:00', 'internal_date_time') : null,
    });
  };

  const stickyHeader = useStickyElement({
    position: 'top',
    offset: 60,
  });

  const showDaysBeforeAfterFields = project.type !== ProjectType.Event || project.default;

  const daysBeforeStartLabel = project.type === ProjectType.Event && project.default
    ? t('backend:days_before_event') : t('backend:days_before_campaign');

  const daysAfterEndLabel = project.type === ProjectType.Event && project.default
    ? t('backend:days_after_event') : t('backend:days_after_campaign');

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card panels header footer>
        <div className="!p-0" ref={stickyHeader.anchorRef}>
          <div
            ref={stickyHeader.elementRef}
            className={classNames(stickyHeader.isSticky && 'fixed z-20', 'max-w-screen-xl mx-auto px-8')}
            style={stickyHeader.isSticky ? stickyHeader.position : {}}
          >
            <div className={classNames('py-4', stickyHeader.isSticky && 'px-8 bg-gradient-to-t from-slate-50 to-white border-b')}>
              <div className="flex justify-between">
                <h3>
                  <Icon className="mr-1">
                    <MailIcon />
                  </Icon>
                  {t(mail.title)}
                </h3>
                <div className="flex items-center">
                  <span className="mr-6 text-sm">
                    <Link href={`${routes.mail_dashboard_page()}?project=${project.id}&mail=${mail.id}`} className="text-slate-800">
                      <Icon className="mr-1">
                        <BarChart2 />
                      </Icon>
                      {t('backend:times_sent', { value: formatNumber(mail.sent_count) })}
                    </Link>
                  </span>
                  <span className={classNames(loadingRecipients && 'opacity-50', 'transition-all mr-6 text-sm text-slate-500')} key={campaignsCount}>
                    <Icon className="mr-1">
                      <Filter />
                    </Icon>
                    {t('backend:campaigns_in_selection_count', {
                      value: formatNumber(campaignsCount),
                    })}
                  </span>
                  <span className={classNames('font-medium mr-4', mail.enabled ? 'text-emerald-600' : 'text-slate-500')}>
                    {!isDraft && (
                      <Icon className="mr-1">
                        {mail.enabled ? <Lock /> : <Unlock />}
                      </Icon>
                    )}
                    {mail.enabled ? t('backend:active') : isDraft ? t('backend:draft') : t('backend:inactive')}
                  </span>

                  <Toggle
                    checked={mail.enabled}
                    onChange={() => handleToggle()}
                    disabled={!mail.enabled && (isDraft || loading)}
                    aria-label={mail.enabled ? t('backend:disable_mail') : t('backend:enable_mail')}
                  />
                  <Modal
                    isOpen={showConfirmationModal}
                    onClose={() => setShowConfirmationModal(false)}
                    size="md"
                  >
                    <div className="space-y-6">
                      <h3 className="py-1">
                        {t('backend:enable_mail')}
                      </h3>

                      <hr />

                      <table className="w-full [&_td]:py-1 [&_td]:align-top">
                        <tbody>
                          <tr>
                            <td className="text-slate-500 w-1/3">
                              {t('backend:mail')}
                            </td>
                            <td>
                              {mail.title}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-slate-500">
                              {t('backend:criteria')}
                            </td>
                            <td>
                              <ul className="list-disc pl-4">
                                {mail.include_individuals && mail.include_teams && (
                                  <li>
                                    {t('backend:individuals_and_teams')}
                                  </li>
                                )}
                                {mail.include_individuals && !mail.include_teams && (
                                  <li>
                                    {t('backend:individuals')}
                                  </li>
                                )}
                                {!mail.include_individuals && mail.include_teams && (
                                  <li>
                                    {t('backend:teams')}
                                  </li>
                                )}
                                {mail.min_days_old !== null && (
                                  <li>
                                    {t('backend:after_x_days', {
                                      count: mail.min_days_old,
                                      value: formatNumber(mail.min_days_old),
                                    })}
                                  </li>
                                )}
                                {mail.days_before_start !== null && (
                                  <li>
                                    {t(project.type === ProjectType.Event ? 'backend:x_days_before_event' : 'backend:x_days_before_campaign', {
                                      count: mail.days_before_start,
                                      value: formatNumber(mail.days_before_start),
                                    })}
                                  </li>
                                )}
                                {mail.days_after_end !== null && (
                                  <li>
                                    {t(project.type === ProjectType.Event ? 'backend:x_days_after_event' : 'backend:x_days_after_campaign', {
                                      count: mail.days_after_end,
                                      value: formatNumber(mail.days_after_end),
                                    })}
                                  </li>
                                )}
                                {mail.created_after !== null && (
                                  <li>
                                    {t('backend:created_after_value', {
                                      value: formatDate(mail.created_after, 'display_date_time'),
                                    })}
                                  </li>
                                )}
                                {mail.max_funds_raised !== null && (
                                  <li>
                                    {(mail.min_funds_raised || 0) === mail.max_funds_raised && (
                                      t('backend:raised_exactly', {
                                        value: formatCurrency(mail.max_funds_raised),
                                      })
                                    )}
                                    {(mail.min_funds_raised || 0) !== mail.max_funds_raised && (
                                      t('backend:raised_range', {
                                        min: formatCurrency(mail.min_funds_raised || 0),
                                        max: formatCurrency(mail.max_funds_raised),
                                      })
                                    )}
                                  </li>
                                )}
                                {mail.min_funds_raised !== null && mail.max_funds_raised === null && (
                                  <li>
                                    {t('backend:raised_at_least', {
                                      min: formatCurrency(mail.min_funds_raised),
                                    })}
                                  </li>
                                )}
                                {mail.products.length > 0 && (
                                  <li>
                                    {t('backend:products_value', {
                                      value: mail.products.map((product) => product.title).join(', '),
                                    })}
                                  </li>
                                )}
                              </ul>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-slate-500">
                              {t('backend:subject')}
                            </td>
                            <td>
                              {mail.subject}
                            </td>
                          </tr>
                          {mail.start_at && mail.end_at && (
                            <>
                              <tr>
                                <td className="text-slate-500">
                                  {t('backend:active_from')}
                                </td>
                                <td>
                                  <Delimit>
                                    <DateRange start={mail.start_at} />
                                    {formatRelativeDate(mail.start_at)}
                                  </Delimit>
                                </td>
                              </tr>
                              <tr>
                                <td className="text-slate-500">
                                  {t('backend:active_until')}
                                </td>
                                <td>
                                  <Delimit>
                                    <DateRange start={mail.end_at} />
                                    {formatRelativeDate(mail.end_at)}
                                  </Delimit>
                                </td>
                              </tr>
                            </>
                          )}
                          {mail.start_at && !mail.end_at && (
                            <tr>
                              <td className="text-slate-500">
                                {t('backend:send_date')}
                              </td>
                              <td>
                                <Delimit>
                                  <DateRange start={mail.start_at} />
                                  {formatRelativeDate(mail.start_at)}
                                </Delimit>
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td className="text-slate-500">
                              {t('backend:campaigns_in_selection')}
                            </td>
                            <td>
                              {formatNumber(campaignsCount)}
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <hr />

                      <div className="space-x-2 text-right">
                        <Button onClick={() => setShowConfirmationModal(false)} variant="secondary">
                          {t('shared:confirmation.cancel')}
                        </Button>
                        <Button onClick={() => handleEnableMail()}>
                          {t('shared:confirmation.confirm')}
                        </Button>
                      </div>
                    </div>
                  </Modal>
                </div>
              </div>
            </div>
          </div>
        </div>

        {serverErrors && (
          <div>
            <ServerErrors errors={serverErrors} scrollIntoView />
          </div>
        )}

        <div className="grid grid-cols-[1fr_2fr] gap-8">
          <div className="text-slate-500">
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:mail_name')}
            </h4>
            {t('backend:mail_name_description')}
          </div>
          <div className="space-y-6">
            <div className="grid grid-cols-[1fr_1fr] gap-6">
              <InputGroup>
                <InputLabel required valid={!errors.title} htmlFor="mail_title">
                  {t('backend:mail_name')}
                </InputLabel>
                <Input
                  {...register('title', { required: true })}
                  id="mail_title"
                  aria-invalid={!!errors.title}
                  disabled={mail.enabled}
                />
                <ErrorMessage error={errors.title} attribute={t('backend:mail_name')} />
              </InputGroup>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-8">
          <div className="text-slate-500">
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:criteria')}
            </h4>
            <p className="mb-4">
              {t('backend:mail_criteria_description')}
            </p>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel>
                {t('backend:campaign_types')}
              </InputLabel>
              <CheckboxLabel htmlFor="mail_include_individuals">
                <Checkbox
                  {...register('include_individuals')}
                  id="mail_include_individuals"
                  aria-invalid={!!errors.include_individuals}
                  disabled={mail.enabled}
                />
                <span>
                  {t('backend:individuals')}
                </span>
              </CheckboxLabel>
              {includeIndividuals && projectTickets.length > 0 && (
                <>
                  <CheckboxLabel htmlFor="mail_all_tickets" className="ml-6">
                    <Checkbox
                      onClick={() => setValue('tickets', [], { shouldDirty: true })}
                      checked={tickets.length === 0}
                      id="mail_all_tickets"
                      disabled={tickets.length === 0 || mail.enabled}
                    />
                    <span>
                      {t('backend:all_individuals')}
                    </span>
                  </CheckboxLabel>
                  {projectTickets.map((ticket) => (
                    <CheckboxLabel htmlFor={`mail_ticket_${ticket.id}`} key={ticket.id} className="ml-6">
                      <Checkbox
                        {...register('tickets')}
                        value={ticket.id}
                        id={`mail_ticket_${ticket.id}`}
                        disabled={mail.enabled}
                      />
                      <span>
                        {ticket.title}
                      </span>
                    </CheckboxLabel>
                  ))}
                </>
              )}
              <ErrorMessage error={errors.include_individuals} attribute={t('backend:individuals')} />
              <CheckboxLabel htmlFor="mail_include_teams">
                <Checkbox
                  {...register('include_teams')}
                  id="mail_include_teams"
                  aria-invalid={!!errors.include_teams}
                  disabled={mail.enabled}
                />
                <span>
                  {t('backend:teams')}
                </span>
              </CheckboxLabel>
              <ErrorMessage error={errors.include_teams} attribute={t('backend:teams')} />
            </InputGroup>

            <div className="grid grid-cols-[1fr_1fr] gap-6">
              <InputGroup>
                <InputLabel htmlFor="mail_min_days_old">
                  {t('backend:min_days_old')}
                </InputLabel>
                <InputWrapper>
                  <Input
                    {...register('min_days_old')}
                    id="mail_min_days_old"
                    className="pr-[150px]"
                    onBlur={(event) => {
                      handleIntegerBlur(event);
                      setValue('min_days_old', event.target.value, { shouldDirty: true });
                    }}
                    aria-invalid={!!errors.min_days_old}
                    min="0"
                    disabled={mail.enabled}
                  />
                  <span className={classNames((minDaysOld === null || mail.enabled) && 'text-slate-500')}>
                    {t('backend:days_ago_lowercase', { count: minDaysOld || 0 })}
                  </span>
                </InputWrapper>
                <ErrorMessage error={errors.min_days_old} attribute={t('backend:min_days_old')} />
              </InputGroup>
            </div>

            <div className="grid grid-cols-[1fr_1fr] gap-6">
              <InputGroup>
                <InputLabel valid={!errors.created_after_date} htmlFor="mail_created_after_date">
                  {t('backend:created_after')}
                </InputLabel>
                <Controller
                  control={control}
                  name="created_after_date"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DateInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ariaInvalid={!!errors.created_after_date}
                      id="mail_created_after_date"
                      disabled={mail.enabled}
                      optional
                    />
                  )}
                />
                <ErrorMessage error={errors.created_after_date} attribute={t('backend:created_after')} />
              </InputGroup>
              <InputGroup>
                <InputLabel valid={!errors.created_after_time} htmlFor="mail_created_after_time">
                  {t('backend:time')}
                </InputLabel>
                <Controller
                  control={control}
                  name="created_after_time"
                  rules={{ max: endDate || undefined }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TimeInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ariaInvalid={!!errors.created_after_time}
                      id="mail_created_after_time"
                      disabled={mail.enabled}
                      optional
                    />
                  )}
                />
                <ErrorMessage error={errors.created_after_time} attribute={t('backend:time')} />
              </InputGroup>
            </div>

            {showDaysBeforeAfterFields && (
              <div className="grid grid-cols-[1fr_1fr] gap-6">
                <InputGroup>
                  <InputLabel htmlFor="mail_days_before_start">
                    {daysBeforeStartLabel}
                  </InputLabel>
                  <InputWrapper>
                    <Input
                      {...register('days_before_start')}
                      id="mail_days_before_start"
                      className="pr-[150px]"
                      onBlur={(event) => {
                        handleIntegerBlur(event);
                        setValue('days_before_start', event.target.value, { shouldDirty: true });
                      }}
                      aria-invalid={!!errors.days_before_start}
                      min="0"
                      disabled={mail.enabled || daysAfterEnd !== null}
                    />
                    <span className={classNames((daysBeforeStart === null || mail.enabled) && 'text-slate-500')}>
                      {t('backend:days_lowercase', { count: daysBeforeStart || 0 })}
                    </span>
                  </InputWrapper>
                  <ErrorMessage error={errors.days_before_start} attribute={daysBeforeStartLabel} />
                </InputGroup>
                <InputGroup>
                  <InputLabel htmlFor="mail_days_after_date">
                    {daysAfterEndLabel}
                  </InputLabel>
                  <InputWrapper>
                    <Input
                      {...register('days_after_end')}
                      id="mail_days_after_date"
                      className="pr-[150px]"
                      onBlur={(event) => {
                        handleIntegerBlur(event);
                        setValue('days_after_end', event.target.value, { shouldDirty: true });
                      }}
                      aria-invalid={!!errors.days_after_end}
                      min="0"
                      disabled={mail.enabled || daysBeforeStart !== null}
                    />
                    <span className={classNames((daysAfterEnd === null || mail.enabled) && 'text-slate-500')}>
                      {t('backend:days_lowercase', { count: daysAfterEnd || 0 })}
                    </span>
                  </InputWrapper>
                  <ErrorMessage error={errors.days_after_end} attribute={daysAfterEndLabel} />
                </InputGroup>
              </div>
            )}

            <div className="grid grid-cols-[1fr_1fr] gap-6">
              <InputGroup>
                <InputLabel htmlFor="mail_min_funds_raised">
                  {t('backend:min_funds_raised')}
                </InputLabel>
                <InputWrapper>
                  <span className={classNames((minFundsRaised === null || mail.enabled) && 'text-slate-500')}>
                    €
                  </span>
                  <Input
                    {...register('min_funds_raised')}
                    id="mail_min_funds_raised"
                    className="pl-7"
                    onBlur={(event) => {
                      handleCurrencyBlur(event);
                      setValue('min_funds_raised', event.target.value, { shouldDirty: true });
                    }}
                    aria-invalid={!!errors.min_funds_raised}
                    min="0"
                    disabled={mail.enabled}
                  />
                </InputWrapper>
                <ErrorMessage error={errors.min_funds_raised} attribute={t('backend:min_funds_raised')} />
              </InputGroup>

              <InputGroup>
                <InputLabel htmlFor="mail_max_funds_raised">
                  {t('backend:max_funds_raised')}
                </InputLabel>
                <InputWrapper>
                  <span className={classNames((maxFundsRaised === null || mail.enabled) && 'text-slate-500')}>
                    €
                  </span>
                  <Input
                    {...register('max_funds_raised')}
                    id="mail_max_funds_raised"
                    className="pl-7"
                    onBlur={(event) => {
                      handleCurrencyBlur(event);
                      setValue('max_funds_raised', event.target.value, { shouldDirty: true });
                    }}
                    aria-invalid={!!errors.max_funds_raised}
                    min="0"
                    disabled={mail.enabled}
                  />
                </InputWrapper>
                <ErrorMessage error={errors.max_funds_raised} attribute={t('backend:max_funds_raised')} />
              </InputGroup>
            </div>

            {products.length > 0 && (
              <InputGroup>
                <InputLabel>
                  {t('backend:products')}
                </InputLabel>
                <InputDescription>
                  {t('backend:campaigns_signed_up_for_or_sold_products')}
                </InputDescription>
                {products.map((product) => (
                  <CheckboxLabel
                    htmlFor={`mail_products_${product.id}`}
                    key={product.id}
                  >
                    <Checkbox
                      {...register('products')}
                      value={product.id}
                      id={`mail_products_${product.id}`}
                      aria-invalid={!!errors.products}
                      disabled={mail.enabled}
                    />
                    <span>
                      {product.title}
                    </span>
                  </CheckboxLabel>
                ))}
                <ErrorMessage error={errors.products} attribute={t('backend:products')} />
              </InputGroup>
            )}

            <InputGroup>
              <InputLabel role="button" onClick={() => setShowOptOuts((show) => !show)} className="cursor-pointer">
                {t('backend:ignore_opt_outs')}
                <Icon className="ml-1">
                  {showOptOuts ? <ChevronUp /> : <ChevronDown />}
                </Icon>
              </InputLabel>
              <FadeIn
                when={showOptOuts}
                render={() => (
                  <>
                    <InputDescription>
                      {t('backend:ignore_opt_outs_description')}
                    </InputDescription>
                    <CheckboxLabel htmlFor="mail_ignore_opt_outs">
                      <Checkbox
                        {...register('ignore_opt_outs')}
                        id="mail_ignore_opt_outs"
                        aria-invalid={!!errors.ignore_opt_outs}
                        disabled={mail.enabled}
                      />
                      <span>
                        {t('backend:ignore_opt_outs_label')}
                      </span>
                    </CheckboxLabel>
                  </>
                )}
              />
              <ErrorMessage error={errors.ignore_opt_outs} attribute={t('backend:ignore_opt_outs')} />
            </InputGroup>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_2fr] gap-8">
          <div className="text-slate-500">
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:schedule')}
            </h4>
            <p>
              {t('backend:mail_schedule_description')}
            </p>
            {project.event && (
              <p className="mt-2">
                <Icon className="mr-1">
                  <Calendar />
                </Icon>
                {t('backend:event_label')}
                {' '}
                <DateRange start={project.event.start_date} end={project.event.end_date} />
              </p>
            )}
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel>
                {t('backend:send_mail')}
              </InputLabel>
              <RadioLabel>
                <Radio
                  checked={continuous}
                  onChange={() => setValue('continuous', true, { shouldDirty: true })}
                  aria-invalid={!!errors.continuous}
                  disabled={mail.enabled}
                />
                <span>
                  {t('backend:continuously')}
                </span>
              </RadioLabel>
              <RadioLabel>
                <Radio
                  checked={!continuous}
                  onChange={() => setValue('continuous', false, { shouldDirty: true })}
                  aria-invalid={!!errors.continuous}
                  disabled={mail.enabled}
                />
                <span>
                  {t('backend:once')}
                </span>
              </RadioLabel>
            </InputGroup>

            <FadeIn
              when={continuous}
              render={() => (
                <div className="space-y-6">
                  <div className="grid grid-cols-[1fr_1fr] gap-6">
                    <InputGroup>
                      <InputLabel required valid={!errors.start_date} htmlFor="mail_start_date">
                        {t('backend:start_date')}
                      </InputLabel>
                      <Controller
                        control={control}
                        name="start_date"
                        rules={{ required: true, max: endDate || undefined }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DateInput
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            ariaInvalid={!!errors.start_date}
                            endDate={endDate}
                            selectsStart
                            disabled={mail.enabled}
                            id="mail_start_date"
                          />
                        )}
                      />
                      <ErrorMessage
                        error={errors.start_date}
                        attribute={t('backend:start_date')}
                        values={{ max: t('backend:end_date') }}
                      />
                    </InputGroup>
                    <InputGroup>
                      <InputLabel valid={!errors.start_time} htmlFor="mail_start_time">
                        {t('backend:start_time')}
                      </InputLabel>
                      <Controller
                        control={control}
                        name="start_time"
                        rules={{ max: endDate || undefined }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <TimeInput
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            ariaInvalid={!!errors.start_time}
                            id="mail_start_time"
                            disabled={mail.enabled}
                            optional
                          />
                        )}
                      />
                      <ErrorMessage error={errors.start_time} attribute={t('backend:start_time')} />
                    </InputGroup>
                  </div>

                  <div className="grid grid-cols-[1fr_1fr] gap-6">
                    <InputGroup>
                      <InputLabel required valid={!errors.end_date} htmlFor="mail_end_date">
                        {t('backend:end_date')}
                      </InputLabel>
                      <Controller
                        control={control}
                        name="end_date"
                        rules={{ required: true, min: startDate || undefined }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DateInput
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            ariaInvalid={!!errors.end_date}
                            startDate={startDate}
                            selectsEnd
                            id="mail_end_date"
                            disabled={mail.enabled}
                          />
                        )}
                      />
                      <ErrorMessage
                        error={errors.end_date}
                        attribute={t('backend:end_date')}
                        values={{ max: t('backend:start_date') }}
                      />
                    </InputGroup>
                    <InputGroup>
                      <InputLabel valid={!errors.end_time} htmlFor="mail_end_time">
                        {t('backend:end_time')}
                      </InputLabel>
                      <Controller
                        control={control}
                        name="end_time"
                        rules={{ max: endDate || undefined }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <TimeInput
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            ariaInvalid={!!errors.end_time}
                            id="mail_end_time"
                            disabled={mail.enabled}
                            optional
                          />
                        )}
                      />
                      <ErrorMessage error={errors.end_time} attribute={t('backend:end_time')} />
                    </InputGroup>
                  </div>

                  <InfoMessage>
                    {t('backend:send_mail_continuously_description')}
                  </InfoMessage>
                </div>
              )}
            />

            <FadeIn
              when={!continuous}
              render={() => (
                <div className="space-y-6">
                  <div className="grid grid-cols-[1fr_1fr] gap-6">
                    <InputGroup>
                      <InputLabel required valid={!errors.start_date} htmlFor="mail_start_date">
                        {t('backend:send_date')}
                      </InputLabel>
                      <Controller
                        control={control}
                        name="start_date"
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DateInput
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            ariaInvalid={!!errors.start_date}
                            endDate={endDate}
                            selectsStart
                            id="mail_start_date"
                          />
                        )}
                      />
                      <ErrorMessage error={errors.start_date} attribute={t('backend:send_date')} />
                    </InputGroup>
                    <InputGroup>
                      <InputLabel valid={!errors.start_time} htmlFor="mail_start_time">
                        {t('backend:send_time')}
                      </InputLabel>
                      <Controller
                        control={control}
                        name="start_time"
                        rules={{ max: endDate || undefined }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <TimeInput
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            ariaInvalid={!!errors.start_time}
                            id="mail_start_time"
                            disabled={mail.enabled}
                            optional
                          />
                        )}
                      />
                      <ErrorMessage error={errors.start_time} attribute={t('backend:send_time')} />
                    </InputGroup>
                  </div>

                  <InfoMessage>
                    {t('backend:send_mail_once_description')}
                  </InfoMessage>
                </div>
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-[1fr_2fr] gap-8">
          <div className="text-slate-500">
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:subject_and_body')}
            </h4>
            {t('backend:subject_and_body_description')}
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel required valid={!errors.subject} htmlFor="mail_subject">
                {t('backend:subject')}
              </InputLabel>
              <Input
                {...register('subject', { required: true })}
                id="mail_subject"
                aria-invalid={!!errors.subject}
                disabled={mail.enabled}
              />
              <ErrorMessage error={errors.subject} attribute={t('backend:subject')} />
            </InputGroup>
            <InputGroup>
              <InputLabel valid={!errors.sender} htmlFor="mail_sender">
                {t('backend:mail_sender')}
              </InputLabel>
              <Input
                {...register('sender')}
                id="mail_sender"
                aria-invalid={!!errors.sender}
                placeholder={defaultSender}
                disabled={mail.enabled}
              />
              <ErrorMessage error={errors.sender} attribute={t('backend:mail_sender')} />
            </InputGroup>
            <InputGroup>
              <InputLabel htmlFor="edit_mail_header">
                {t('backend:header_image')}
              </InputLabel>
              <InputDescription>
                {t('backend:custom_mail_header_description')}
              </InputDescription>
              <div className="max-w-[604px] p-4 border rounded-md border-slate-250">
                <div
                  className="flex items-center justify-center mb-4 transition-all rounded-sm bg-slate-50"
                >
                  {!mailHeader && !deleteMailHeader && currentMailHeaderUrl && (
                    <img
                      src={currentMailHeaderUrl}
                      alt={project.title}
                      className="w-full rounded-sm"
                    />
                  )}
                  {mailHeader && mailHeaderSrc && (
                    <img
                      src={mailHeaderSrc}
                      alt={project.title}
                      className="w-full rounded-sm"
                      onLoad={validateMailHeader}
                    />
                  )}
                  {((!mailHeader && !currentMailHeaderUrl) || deleteMailHeader) && (
                    <Icon className="!w-12 !h-12 text-slate-250 my-16">
                      <Image strokeWidth={1} />
                    </Icon>
                  )}
                </div>
                <div className="flex space-x-2">
                  <FileInput
                    {...register('header')}
                    accept=".jpg,.jpeg,.png"
                    id="edit_mail_header"
                    aria-invalid={!!errors.header}
                    onChange={handleMailHeaderChange}
                    disabled={mail.enabled}
                  >
                    <Icon className="mr-1">
                      <Image />
                    </Icon>
                    {t('shared:form.select')}
                  </FileInput>
                  {canDeleteMailHeader && (
                    <Button onClick={() => unsetMailHeader()} disabled={mail.enabled} variant="tertiary">
                      <Icon className="mr-1">
                        <Trash />
                      </Icon>
                      {t('shared:form.delete')}
                    </Button>
                  )}
                </div>
              </div>
              <ErrorMessage error={errors.header} attribute={t('backend:project_mail_header')} />
            </InputGroup>
            <InputGroup>
              <InputLabel valid={!errors.header_link} htmlFor="mail_header_link">
                {t('backend:header_link')}
              </InputLabel>
              <InputDescription>
                {t('backend:header_link_description')}
              </InputDescription>
              <Input
                {...register('header_link')}
                id="mail_header_link"
                aria-invalid={!!errors.header_link}
                placeholder={project.website || ''}
                disabled={mail.enabled}
              />
              <ErrorMessage error={errors.header_link} attribute={t('backend:header_link')} />
            </InputGroup>
            <InputGroup>
              <InputLabel required valid={!errors.body}>
                {t('backend:body')}
              </InputLabel>
              <MailEditor
                type="custom_mail"
                editor={(
                  <Controller
                    control={control}
                    name="body"
                    rules={{ required: true }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <Editor
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        allowProjectColor
                        variables={project.custom_mail_variables}
                      />
                    )}
                  />
                )}
                preview={body}
                project={project}
                charity={charity}
                headerUrl={(mailHeader && mailHeaderSrc) || (!deleteMailHeader && mail.header_url) || undefined}
                user={user}
                disabled={mail.enabled}
              />
              <ErrorMessage error={errors.body} attribute={t('backend:body')} />
            </InputGroup>
          </div>
        </div>

        <div className="flex items-center justify-between text-slate-500">
          {mail.deletable && (
            <div>
              <ConfirmButton
                onClick={() => deleteMail()}
                loading={loading}
                variant="inline"
              >
                {t('backend:delete_mail')}
              </ConfirmButton>
            </div>
          )}
          <div className="flex items-center ml-auto space-x-6">
            {mail.enabled && (
              <div>
                {t('backend:deactivate_mail_to_edit')}
              </div>
            )}
            {isDraft && (
              <div>
                {t('backend:complete_mail_to_activate')}
              </div>
            )}
            <Button
              type="submit"
              loading={loading}
              disabled={mail.enabled}
            >
              {t('backend:save')}
            </Button>
          </div>
        </div>
      </Card>
    </form>
  );
}

EditCustomMailForm.layout = (page: ReactNode) => <Layout><ProjectLayout>{page}</ProjectLayout></Layout>;
